import type {
  OrgUnitTreeNode,
  Page,
  Role,
  RoleInput,
  RoleUpdate,
  User,
  UserAccessView,
  UserCreate,
  UserInvitationIssued,
  UserInvitationView,
  UserListItem,
  UserSaved,
  UserUpdate,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { UserListParams } from './search'

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (query: UserListParams) => [...userKeys.lists(), query] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
  access: (id: string) => [...userKeys.all, 'access', id] as const,
  invitation: (id: string) => [...userKeys.all, 'invitation', id] as const,
}

export const roleKeys = {
  all: ['roles'] as const,
  list: () => [...roleKeys.all, 'list'] as const,
  detail: (id: string) => [...roleKeys.all, 'detail', id] as const,
}

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

export function userListQueryOptions(query: UserListParams) {
  return queryOptions({
    queryKey: userKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<UserListItem>>('/users', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
          roleId: query.roleId,
          orgUnitId: query.orgUnitId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function userQueryOptions(id: string) {
  return queryOptions({
    queryKey: userKeys.detail(id),
    queryFn: ({ signal }) => api.get<User>(path('/users', id), { signal }),
  })
}

/** `GET /users/:id/access`: what the user reaches, per role assignment and per permission (ADR-025). */
export function userAccessQueryOptions(id: string) {
  return queryOptions({
    queryKey: userKeys.access(id),
    queryFn: ({ signal }) => api.get<UserAccessView>(path('/users', id, 'access'), { signal }),
  })
}

/** The active org units in path order, for the scope editor's unit picker. */
export const scopeUnitsQueryOptions = queryOptions({
  queryKey: ['org-units', 'tree', 'active'] as const,
  queryFn: ({ signal }) => api.get<OrgUnitTreeNode[]>('/org-units/tree', { signal }),
  staleTime: 60_000,
})

export const rolesQueryOptions = queryOptions({
  queryKey: roleKeys.list(),
  queryFn: ({ signal }) => api.get<Role[]>('/roles', { signal }),
  staleTime: 60_000,
})

export function roleQueryOptions(id: string) {
  return queryOptions({
    queryKey: roleKeys.detail(id),
    queryFn: ({ signal }) => api.get<Role>(path('/roles', id), { signal }),
  })
}

/** User lists and role member counts move together. */
function useInvalidateAccess() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: roleKeys.all }),
    ])
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateAccess()
  return useMutation({
    mutationFn: (input: UserCreate) => api.post<UserSaved>('/users', input),
    onSuccess: ({ warnings: _warnings, ...user }) => {
      queryClient.setQueryData(userKeys.detail(user.id), user)
      void invalidate()
    },
  })
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateAccess()
  return useMutation({
    mutationFn: (input: UserUpdate) => api.put<UserSaved>(path('/users', id), input),
    onSuccess: ({ warnings: _warnings, ...user }) => {
      queryClient.setQueryData(userKeys.detail(user.id), user)
      void invalidate()
      void queryClient.invalidateQueries({ queryKey: userKeys.access(user.id) })
    },
  })
}

export function useResetUserPassword(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (password: string) => api.post<void>(path('/users', id, 'reset-password'), { password }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
      ]),
  })
}

export function useUnlockUser(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>(path('/users', id, 'unlock')),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
      ]),
  })
}

/**
 * `POST /users/:id/invite` (R0b MS9): e-mails the user a link to set their own password (valid 72 h);
 * a new invitation revokes the previous link.
 */
export function useInviteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<UserInvitationIssued>(path('/users', id, 'invite')),
    onSuccess: (_result, id) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: userKeys.invitation(id) }),
      ]),
  })
}

/** `GET /users/:id/invitation`: the latest invitation and whether its e-mail went out. */
export function userInvitationQueryOptions(id: string) {
  return queryOptions({
    queryKey: userKeys.invitation(id),
    queryFn: ({ signal }) => api.get<UserInvitationView>(path('/users', id, 'invitation'), { signal }),
  })
}

/** `POST /users/:id/invitation/revoke`: the open invitation link stops working. */
export function useRevokeInvitation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>(path('/users', id, 'invitation/revoke')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: userKeys.invitation(id) }),
  })
}

export function useSaveRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: RoleInput | RoleUpdate }) =>
      id ? api.put<Role>(path('/roles', id), input) : api.post<Role>('/roles', input),
    onSuccess: (role) => {
      queryClient.setQueryData(roleKeys.detail(role.id), role)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: roleKeys.list() }),
        // Role names appear on user rows and records.
        queryClient.invalidateQueries({ queryKey: userKeys.all }),
      ])
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(path('/roles', id)),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: roleKeys.detail(id) })
      return queryClient.invalidateQueries({ queryKey: roleKeys.list() })
    },
  })
}
